import React, { useState, type CSSProperties } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ScopeRow } from "../primitives";
import type { Status } from "../primitives";
import type { LibraryNode } from "../../../lib/types";
import { agentPersona } from "../../lib/agent-persona";

function DraggableAgentRow({
  agent,
  children,
}: {
  agent: LibraryNode;
  children: (props: { listeners: Record<string, unknown> | undefined; attributes: Record<string, unknown> }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: agent.id });
  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.4 : 1 }}>
      {children({ listeners, attributes: attributes as unknown as Record<string, unknown> })}
    </div>
  );
}

interface ModuleSectionProps {
  module: LibraryNode;
  agents: LibraryNode[];
  selectedId?: string | null;
  highlightedIds?: Set<string>;
  onAgentClick: (agentId: string) => void;
  onAddAgent?: (parentId: string) => void;
}

function getModuleColor(module: LibraryNode): string {
  const persona = agentPersona(module);
  return `var(--c-${persona}-600)`;
}

export function ModuleSection({
  module,
  agents,
  selectedId,
  highlightedIds,
  onAgentClick,
  onAddAgent,
}: ModuleSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const color = getModuleColor(module);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: module.id });

  const header: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 0",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };

  const badge: CSSProperties = {
    width: 26,
    height: 26,
    borderRadius: 6,
    background: color,
    flexShrink: 0,
  };

  const agentList: CSSProperties = {
    position: "relative",
    marginLeft: 13,
    paddingLeft: 16,
    borderLeft: "0.5px solid var(--color-border-tertiary)",
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={header} onClick={() => setCollapsed(!collapsed)}>
        <div style={badge} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {module.name}
          </div>
          {module.description ? (
            <div
              title={module.description}
              style={{
                fontSize: 12,
                color: "var(--color-text-tertiary)",
                marginTop: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {module.description}
            </div>
          ) : null}
        </div>
        <i
          className={`ti ti-chevron-${collapsed ? "right" : "down"}`}
          style={{ fontSize: 14, color: "var(--color-text-tertiary)" }}
          aria-hidden="true"
        />
      </div>

      {!collapsed && (
        <div
          ref={setDropRef}
          style={{
            ...agentList,
            outline: isOver ? "2px solid var(--color-border-emphasis)" : undefined,
            borderRadius: isOver ? "var(--border-radius-md)" : undefined,
          }}
        >
          {agents.map((agent) => {
            const persona = agentPersona(agent);
            const isSelected = agent.id === selectedId;
            const isHighlighted = highlightedIds?.has(agent.id) ?? false;
            const status: Status = agent.status === "active" ? "active" : "idle";

            return (
              <DraggableAgentRow key={agent.id} agent={agent}>
                {({ listeners, attributes }) => (
                  <div
                    {...listeners}
                    {...attributes}
                    style={{
                      background: isHighlighted ? "var(--color-background-secondary)" : undefined,
                      borderRadius: "var(--border-radius-md)",
                      transition: "background 120ms ease",
                      cursor: "grab",
                    }}
                  >
                    <ScopeRow
                      agent={{
                        id: agent.id,
                        name: agent.name,
                        role: agent.description ?? undefined,
                        persona,
                      }}
                      selected={isSelected}
                      status={status}
                      onClick={() => onAgentClick(agent.id)}
                      showConnector
                    />
                  </div>
                )}
              </DraggableAgentRow>
            );
          })}
          {agents.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "8px 10px" }}>
              No agents under {module.name} yet.{" "}
              {onAddAgent ? (
                <span
                  style={{ color: "var(--color-text-info)", cursor: "pointer" }}
                  onClick={() => onAddAgent(module.id)}
                >
                  Add agent
                </span>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
